import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ApplyAttendanceCorrectionInput,
  AttendanceCorrectionBreak,
  AttendanceCorrectionHistoryResult,
  AttendanceCorrectionMode,
  AttendanceCorrectionRequestDetails,
  AttendanceCorrectionRequestFilters,
  AttendanceCorrectionRequestListItem,
  AttendanceCorrectionRemovedBreak,
} from '../types/attendanceCorrection';
import { normalizeClockTime } from '../utils/checkoutBreak';
import { calendarDateFromApi } from '../utils/dateTime';
import {
  createDefaultAttendanceCorrectionFilters,
  normalizeAttendanceCorrectionFilters,
} from '../utils/attendanceCorrectionFilters';
import { getAttendanceCorrectionStatusLabel } from '../utils/attendanceCorrectionStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function toCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}

function camelizeRow(row: ObjectValue): ObjectValue {
  const flattened: ObjectValue = { ...row };
  for (const [key, value] of Object.entries(row)) {
    flattened[toCamelKey(key)] = value;
  }
  return flattened;
}

function objectRow(value: unknown): ObjectValue | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as ObjectValue;
}

function cancellationReasonFrom(row: ObjectValue | null): string | null {
  if (!row) return null;
  return textOrNull(
    row.cancellationReason ??
      row.cancellation_reason ??
      row.cancelReason ??
      row.cancel_reason ??
      row.cancelledReason ??
      row.canceledReason ??
      row.cancelled_reason ??
      row.canceled_reason,
  );
}

function dateOnly(value: unknown): string | null {
  return calendarDateFromApi(value);
}

function timeOnly(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  const match = text.match(/(\d{2}:\d{2})(?::\d{2})?/);
  return match?.[1] ?? null;
}

function reasonSummary(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function isPendingCancellable(statusCode: string | null): boolean {
  return (statusCode || '').toUpperCase() === 'PENDING';
}

function correctionMode(
  value: unknown,
): AttendanceCorrectionMode | null {
  const text = textOrNull(value)?.toUpperCase();
  if (text === 'CHECKOUT_ONLY' || text === 'FULL_DAY') return text;
  return null;
}

function parseJsonValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '[' && trimmed[0] !== '{')) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
}

function splitClocks(value: unknown): string[] {
  const text = textOrNull(value);
  if (!text) return [];
  return text
    .split(',')
    .map(part => timeOnly(part))
    .filter((part): part is string => Boolean(part));
}

function submittedFlagValue(value: unknown): unknown {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return 1;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return 0;
  }
  return value ?? null;
}

function breakFromUnknown(value: unknown): AttendanceCorrectionBreak | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as ObjectValue;
  const startTime = timeOnly(
    row.startTime ?? row.breakStartTime ?? row.start,
  );
  const endTime = timeOnly(row.endTime ?? row.breakEndTime ?? row.end);
  if (!startTime || !endTime) return null;
  return {
    startTime,
    endTime,
    existingStartTime: timeOnly(
      row.existingStartTime ?? row.existing_start_time,
    ),
    existingEndTime: timeOnly(row.existingEndTime ?? row.existing_end_time),
    startSubmitted: submittedFlagValue(
      row.startSubmitted ?? row.start_submitted,
    ),
    endSubmitted: submittedFlagValue(row.endSubmitted ?? row.end_submitted),
    startSource: textOrNull(row.startSource ?? row.start_source),
    startOriginalSource: textOrNull(
      row.startOriginalSource ?? row.start_original_source,
    ),
    startIsManuallyEdited:
      row.startIsManuallyEdited ?? row.start_is_manually_edited ?? null,
    endSource: textOrNull(row.endSource ?? row.end_source),
    endOriginalSource: textOrNull(
      row.endOriginalSource ?? row.end_original_source,
    ),
    endIsManuallyEdited:
      row.endIsManuallyEdited ?? row.end_is_manually_edited ?? null,
  };
}

function normalizeBreaks(
  row: ObjectValue,
  fallbackStart: string | null,
  fallbackEnd: string | null,
): AttendanceCorrectionBreak[] {
  const raw = parseJsonValue(row.breaks ?? row.requestedBreaks);
  if (Array.isArray(raw)) {
    const parsed = raw
      .map(breakFromUnknown)
      .filter((item): item is AttendanceCorrectionBreak => item !== null);
    if (parsed.length > 0) return parsed;
  }

  const starts = splitClocks(row.breakStartTime ?? row.requestedBreakStartTime);
  const ends = splitClocks(row.breakEndTime ?? row.requestedBreakEndTime);
  if (starts.length > 1 && starts.length === ends.length) {
    return starts.map((startTime, index) => ({
      startTime,
      endTime: ends[index],
    }));
  }

  if (fallbackStart && fallbackEnd) {
    return [{ startTime: fallbackStart, endTime: fallbackEnd }];
  }
  return [];
}

function normalizeRemovedBreaks(
  row: ObjectValue,
): AttendanceCorrectionRemovedBreak[] {
  const raw = parseJsonValue(row.removedBreaks ?? row.removed_breaks);
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const value = item as ObjectValue;
    const startTime = timeOnly(value.startTime ?? value.breakStartTime);
    if (!startTime) return [];
    return [
      { startTime, endTime: timeOnly(value.endTime ?? value.breakEndTime) },
    ];
  });
}

function normalizeRequest(
  row: ObjectValue,
): AttendanceCorrectionRequestListItem | null {
  const correctionRequestId = numberOrNull(
    row.correctionRequestId ?? row.attendanceCorrectionRequestId ?? row.requestId,
  );
  if (correctionRequestId === null || correctionRequestId <= 0) return null;

  const statusCode =
    textOrNull(row.statusCode) ?? textOrNull(row.status) ?? null;
  const breakStartTime = timeOnly(
    row.breakStartTime ?? row.requestedBreakStartTime,
  );
  const breakEndTime = timeOnly(
    row.breakEndTime ?? row.requestedBreakEndTime,
  );
  const breaks = normalizeBreaks(row, breakStartTime, breakEndTime);

  return {
    correctionRequestId,
    requestNumber: textOrNull(
      row.requestNumber ?? row.correctionRequestNumber,
    ),
    attendanceDate: dateOnly(
      row.attendanceDate ?? row.requestDate ?? row.correctionDate,
    ),
    checkInTime: timeOnly(row.checkInTime ?? row.requestedCheckInTime),
    checkOutTime: timeOnly(row.checkOutTime ?? row.requestedCheckOutTime),
    breakStartTime: breaks[0]?.startTime ?? breakStartTime,
    breakEndTime: breaks[0]?.endTime ?? breakEndTime,
    breaks,
    correctionMode: correctionMode(row.correctionMode ?? row.requestMode),
    statusCode,
    statusLabel: getAttendanceCorrectionStatusLabel(statusCode),
    applicationDate: dateOnly(
      row.applicationDate ?? row.appliedOn ?? row.createdAt ?? row.submittedAt,
    ),
    reasonSummary: reasonSummary(row.reason ?? row.remarks),
    canCancel: isPendingCancellable(statusCode),
  };
}

function normalizeDetails(
  row: ObjectValue,
): AttendanceCorrectionRequestDetails | null {
  const base = normalizeRequest(row);
  if (!base) return null;

  const workflowInstanceId = numberOrNull(
    row.workflowInstanceId ?? row.workflowId,
  );
  const currentStepName = textOrNull(
    row.currentStepName ?? row.workflowStepName,
  );
  const currentApproverName = textOrNull(
    row.currentApproverName ?? row.approverName,
  );
  const hasWorkflow =
    workflowInstanceId !== null ||
    currentStepName !== null ||
    currentApproverName !== null;

  return {
    correctionRequestId: base.correctionRequestId,
    requestNumber: base.requestNumber,
    applicationDate: base.applicationDate,
    attendanceDate: base.attendanceDate,
    checkInTime: base.checkInTime,
    checkOutTime: base.checkOutTime,
    breakStartTime: base.breakStartTime,
    breakEndTime: base.breakEndTime,
    breaks: base.breaks,
    removedBreaks: normalizeRemovedBreaks(row),
    correctionMode: base.correctionMode,
    statusCode: base.statusCode,
    statusLabel: base.statusLabel,
    reason: textOrNull(row.reason),
    remarks: textOrNull(row.remarks ?? row.notes),
    submittedAt: textOrNull(
      row.submittedAt ?? row.appliedAt ?? row.createdAt ?? row.applicationDate,
    ),
    rejectReason: textOrNull(
      row.rejectReason ?? row.rejectionReason ?? row.rejectedReason,
    ),
    cancellationReason: cancellationReasonFrom(row),
    existingCheckInTime: timeOnly(
      row.existingCheckInTime ?? row.existingCheckInAt,
    ),
    existingCheckOutTime: timeOnly(
      row.existingCheckOutTime ?? row.existingCheckOutAt,
    ),
    existingBreakStartTime: timeOnly(
      row.existingBreakStartTime ?? row.existingBreakStartAt,
    ),
    existingBreakEndTime: timeOnly(
      row.existingBreakEndTime ?? row.existingBreakEndAt,
    ),
    checkInSubmitted: submittedFlagValue(row.checkInSubmitted),
    checkOutSubmitted: submittedFlagValue(row.checkOutSubmitted),
    breakStartSubmitted: submittedFlagValue(row.breakStartSubmitted),
    breakEndSubmitted: submittedFlagValue(row.breakEndSubmitted),
    checkInSource: textOrNull(
      row.checkInSource ?? row.checkInAttendanceSource,
    ),
    checkInOriginalSource: textOrNull(row.checkInOriginalSource),
    checkInIsManuallyEdited:
      row.checkInIsManuallyEdited ?? row.checkInManuallyEdited ?? null,
    checkInKioskId: row.checkInKioskId ?? row.check_in_kiosk_id ?? null,
    checkInFaceVerified:
      row.checkInFaceVerified ?? row.check_in_face_verified ?? null,
    checkInFaceVerificationLogId:
      row.checkInFaceVerificationLogId ??
      row.check_in_face_verification_log_id ??
      null,
    checkInDeviceIdentifier: textOrNull(
      row.checkInDeviceIdentifier ?? row.check_in_device_identifier,
    ),
    checkOutSource: textOrNull(
      row.checkOutSource ?? row.checkOutAttendanceSource,
    ),
    checkOutOriginalSource: textOrNull(row.checkOutOriginalSource),
    checkOutIsManuallyEdited:
      row.checkOutIsManuallyEdited ?? row.checkOutManuallyEdited ?? null,
    checkOutKioskId: row.checkOutKioskId ?? row.check_out_kiosk_id ?? null,
    checkOutFaceVerified:
      row.checkOutFaceVerified ?? row.check_out_face_verified ?? null,
    checkOutFaceVerificationLogId:
      row.checkOutFaceVerificationLogId ??
      row.check_out_face_verification_log_id ??
      null,
    checkOutDeviceIdentifier: textOrNull(
      row.checkOutDeviceIdentifier ?? row.check_out_device_identifier,
    ),
    editedByName: textOrNull(row.editedByName),
    requesterName: textOrNull(row.requesterName),
    workflow: hasWorkflow
      ? {
          workflowInstanceId,
          currentStepName,
          currentApproverName,
        }
      : null,
    canCancel: base.canCancel,
  };
}

function unwrapRow(
  payload: { request?: ObjectValue } | ObjectValue,
): ObjectValue {
  const root = objectRow(payload) ?? {};
  const nestedRequest = objectRow(root.request);
  const nestedWorkflow = objectRow(root.workflow);
  const merged = camelizeRow({
    ...root,
    ...(nestedWorkflow ?? {}),
    ...(nestedRequest ?? {}),
  });
  const cancellationReason =
    cancellationReasonFrom(nestedRequest) ??
    cancellationReasonFrom(root) ??
    cancellationReasonFrom(nestedWorkflow);
  if (cancellationReason) {
    merged.cancellationReason = cancellationReason;
  }
  return merged;
}

export async function getAttendanceCorrectionRequests(
  options: {
    filters?: Partial<AttendanceCorrectionRequestFilters>;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<AttendanceCorrectionHistoryResult> {
  const defaults = createDefaultAttendanceCorrectionFilters();
  const normalized = normalizeAttendanceCorrectionFilters({
    fromDate: options.filters?.fromDate ?? defaults.fromDate,
    toDate: options.filters?.toDate ?? defaults.toDate,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.HISTORY, {
    params: {
      fromDate: normalized.filters.fromDate,
      toDate: normalized.filters.toDate,
      page: normalized.page,
      pageSize: normalized.pageSize,
    },
  });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeRequest)
        .filter(
          (item): item is AttendanceCorrectionRequestListItem => item !== null,
        )
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

export async function getAttendanceCorrectionRequestDetails(
  correctionRequestId: number,
): Promise<AttendanceCorrectionRequestDetails> {
  if (!Number.isInteger(correctionRequestId) || correctionRequestId <= 0) {
    throw Object.assign(
      new Error('correctionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.get<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.REQUEST_DETAILS(correctionRequestId));

  const details = normalizeDetails(unwrapRow(response.data.data ?? {}));
  if (!details) {
    throw Object.assign(
      new Error('Attendance correction request was not found.'),
      { code: 'ATTENDANCE_CORRECTION_NOT_FOUND' },
    );
  }
  return details;
}

export async function applyAttendanceCorrectionRequest(
  input: ApplyAttendanceCorrectionInput,
): Promise<AttendanceCorrectionRequestDetails> {
  const reason = textOrNull(input.reason);
  if (!reason) {
    throw Object.assign(new Error('reason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const checkInTime = normalizeClockTime(input.checkInTime);
  const checkOutTime = normalizeClockTime(input.checkOutTime);
  if (!checkInTime) {
    throw Object.assign(new Error('Check-in time is required.'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const breaks = input.breaks.flatMap(item => {
    const startTime = normalizeClockTime(item.startTime);
    const endTime = normalizeClockTime(item.endTime);
    if (!startTime && !endTime) return [];
    if (!startTime || !endTime) {
      throw Object.assign(
        new Error(
          'Enter both the break start and end times, or leave both empty.',
        ),
        { code: 'VALIDATION_ERROR' },
      );
    }
    // The recorded break this row edits, so approval changes only that break.
    const existingStartTime = normalizeClockTime(item.existingStartTime);
    if (!existingStartTime) return [{ startTime, endTime }];
    return [
      {
        startTime,
        endTime,
        existingStartTime,
        existingEndTime: normalizeClockTime(item.existingEndTime),
      },
    ];
  });
  const removedBreaks = input.removedBreaks?.flatMap(item => {
    const startTime = normalizeClockTime(item.startTime);
    if (!startTime) return [];
    return [
      { startTime, endTime: normalizeClockTime(item.endTime) },
    ];
  });

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.APPLY, {
    attendanceDate: input.attendanceDate,
    checkInTime,
    checkOutTime,
    breakStartTime: breaks.map(item => item.startTime).join(',') || null,
    breakEndTime: breaks.map(item => item.endTime).join(',') || null,
    breaks,
    ...(removedBreaks ? { removedBreaks } : {}),
    reason,
  });

  const details = normalizeDetails(unwrapRow(response.data.data ?? {}));
  if (!details) {
    throw Object.assign(
      new Error('Attendance correction request could not be created.'),
      { code: 'ATTENDANCE_CORRECTION_APPLY_FAILED' },
    );
  }
  return details;
}

export async function cancelAttendanceCorrectionRequest(
  correctionRequestId: number,
  reason: string,
): Promise<AttendanceCorrectionRequestDetails> {
  if (!Number.isInteger(correctionRequestId) || correctionRequestId <= 0) {
    throw Object.assign(
      new Error('correctionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const cancellationReason = textOrNull(reason);
  if (!cancellationReason) {
    throw Object.assign(new Error('cancellationReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.CANCEL(correctionRequestId), {
    cancellationReason,
  });

  const details = normalizeDetails(unwrapRow(response.data.data ?? {}));
  if (!details) {
    throw Object.assign(
      new Error('Attendance correction request was not found.'),
      { code: 'ATTENDANCE_CORRECTION_NOT_FOUND' },
    );
  }
  return details;
}

export async function approveAttendanceCorrectionRequest(
  correctionRequestId: number,
): Promise<AttendanceCorrectionRequestDetails> {
  if (!Number.isInteger(correctionRequestId) || correctionRequestId <= 0) {
    throw Object.assign(
      new Error('correctionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.APPROVE(correctionRequestId));

  const details = normalizeDetails(unwrapRow(response.data.data ?? {}));
  if (!details) {
    throw Object.assign(
      new Error('Attendance correction request was not found.'),
      { code: 'ATTENDANCE_CORRECTION_NOT_FOUND' },
    );
  }
  return details;
}

export async function rejectAttendanceCorrectionRequest(
  correctionRequestId: number,
  reason: string,
): Promise<AttendanceCorrectionRequestDetails> {
  if (!Number.isInteger(correctionRequestId) || correctionRequestId <= 0) {
    throw Object.assign(
      new Error('correctionRequestId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const rejectionReason = textOrNull(reason);
  if (!rejectionReason) {
    throw Object.assign(new Error('rejectionReason is required'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const response = await apiClient.post<
    Envelope<{ request?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.ATTENDANCE.CORRECTIONS.REJECT(correctionRequestId), {
    rejectionReason,
  });

  const details = normalizeDetails(unwrapRow(response.data.data ?? {}));
  if (!details) {
    throw Object.assign(
      new Error('Attendance correction request was not found.'),
      { code: 'ATTENDANCE_CORRECTION_NOT_FOUND' },
    );
  }
  return details;
}
